import { viewports } from '@telus-uds/system-constants';
import useTheme from './useTheme';
import { getComponentTheme, mergeAppearances, resolveThemeTokens } from './utils';
const getResponsiveThemeTokens = function (_ref, tokensProp) {
  let {
    rules = [],
    tokens: defaultThemeTokens = {}
  } = _ref;
  let variants = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let states = arguments.length > 3 ? arguments[3] : undefined;
  const appearances = mergeAppearances(variants, states);
  const tokensByViewport = Object.fromEntries(viewports.keys.map(viewport => [viewport, {
    ...defaultThemeTokens
  }]));

  // Go through each rule and collect them for the corresponding viewport if they apply
  rules.forEach(rule => {
    if (doesRuleApply(rule, appearances)) {
      // If the rule does not have a viewport specified, we collect it in all viewports
      let targetViewports = rule.if.viewport || viewports.keys;
      if (!Array.isArray(targetViewports)) {
        targetViewports = [targetViewports];
      }
      targetViewports.forEach(viewport => {
        tokensByViewport[viewport] = {
          ...tokensByViewport[viewport],
          ...rule.tokens
        };
      });
    }
  });
  Object.keys(tokensByViewport).forEach(viewport => {
    tokensByViewport[viewport] = resolveThemeTokens(tokensByViewport[viewport], appearances, tokensProp);
  });
  return tokensByViewport;
};
const doesRuleApply = (rule, appearances) => Object.entries(rule.if).every(condition => doesConditionApply(condition, appearances));
const doesConditionApply = (_ref2, appearances) => {
  let [key, value] = _ref2;
  if (key === 'viewport') {
    return true;
  }
  // use null rather than undefined so we can serialise the value in themes
  const appearanceValue = appearances[key] ?? null;
  return Array.isArray(value) ? value.includes(appearanceValue) : value === appearanceValue;
};

/**
 * @typedef {import('../utils/props/tokens.js').TokensSet} TokensSet
 * @typedef {import('../utils/props/tokens.js').TokensProp} TokensProp
 * @typedef {import('../utils/props/variantProp').AppearanceSet} AppearanceSet
 *
 * Returns a complete set of tokens for a component for each viewport based on which of the
 * component's theme rules apply to the current set of theme appearances.
 * Pass the returned output to createMediaQueryStyles to generate media query styles for use inside
 * the media query stylesheet from './utils/ssr-media-query'.
 *
 * @typedef {Object} ResponsiveObject
 * @property {TokensSet} xs
 * @property {TokensSet} sm
 * @property {TokensSet} md
 * @property {TokensSet} lg
 * @property {TokensSet} xl
 *
 * @param { string } componentName
 * @param { TokensProp } tokens
 * @param { AppearanceSet } variants
 * @param { AppearanceSet } states
 * @returns { ResponsiveObject }
 */

const useResponsiveThemeTokens = function (componentName) {
  let tokens = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let variants = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let states = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  const theme = useTheme();
  const componentTheme = getComponentTheme(theme, componentName);
  const themeTokens = getResponsiveThemeTokens(componentTheme, tokens, variants, states);
  return themeTokens;
};
export default useResponsiveThemeTokens;